/*
  PDP media viewer (SLEE-138) — full-screen zoom for gallery images, full-view playback for
  gallery videos. Replaces the old desktop hover-to-zoom panel.

  CWV notes:
  - Nothing heavy loads with the page. PhotoSwipe (assets/photoswipe.esm.min.js, ~16KB gz) and
    its CSS are fetched only once the shopper hovers / touches the gallery (warm-up) or opens it.
  - No per-image HTML: the zoom srcset is built here from each gallery <img>'s own Shopify URL
    and width/height attributes, so the LCP image and the HTML payload are unchanged.
  - Click handlers only flip a loading class; PhotoSwipe is created in a later task (after the
    interaction has painted) to keep INP low. Analytics are pushed after paint too.
  - The viewer is a fixed overlay (no layout shift) and PhotoSwipe animates with transforms.
  - Images zoom (up to 2x the largest rendition). Videos play in full view and are never
    zoomable — PhotoSwipe only zooms image content.
*/

const WIDTHS = [800, 1200, 1600, 2048];
const MAX_W = 2048;

const CLOSE_SVG =
  '<svg aria-hidden="true" class="pswp__icn" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>';
const ARROW_SVG = (d) =>
  `<svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="${d}"/></svg>`;
const MUTED_SVG =
  '<svg class="pdp-mv-media__icn-muted" aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>';
const UNMUTED_SVG =
  '<svg class="pdp-mv-media__icn-unmuted" aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>';

const esc = (s) => String(s == null ? '' : s).replace(/[&"<>]/g, (c) => ({ '&': '&amp;', '"': '&quot;', '<': '&lt;', '>': '&gt;' })[c]);

// After the interaction's next paint — keeps work out of the INP window.
const afterPaint = () => new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));

let libPromise = null;
function loadCss(href) {
  return new Promise((resolve) => {
    if (!href || document.querySelector(`link[data-pdp-mv-css]`)) return resolve();
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.pdpMvCss = '';
    link.onload = link.onerror = () => resolve();
    document.head.appendChild(link);
  });
}
function loadLib(wrap) {
  if (!libPromise) {
    libPromise = Promise.all([import(wrap.dataset.mvJs), loadCss(wrap.dataset.mvCss)]).then(([m]) => m.default);
    libPromise.catch(() => { libPromise = null; });
  }
  return libPromise;
}

function track(action, extra) {
  if (!window.SCAnalytics) return;
  const payload = Object.assign({ section_name: 'pdp_media', media_action: action }, extra);
  afterPaint().then(() => window.SCAnalytics.push('pdp_media_engage', payload));
}

/* ---------- gallery → viewer items ---------- */

function slidesOf(sliderEl) {
  if (!sliderEl) return [];
  if (sliderEl.swiper && sliderEl.swiper.slides) return Array.from(sliderEl.swiper.slides);
  return Array.from(sliderEl.querySelectorAll('.swiper-wrapper > .swiper-slide'));
}

function imageItem(slide) {
  const img = slide.querySelector('img');
  if (!img) return null;
  const w = parseInt(img.getAttribute('width'), 10) || img.naturalWidth;
  const h = parseInt(img.getAttribute('height'), 10) || img.naturalHeight;
  const raw = img.getAttribute('src');
  if (!w || !h || !raw) return null;
  const url = new URL(raw, location.href);
  const at = (width) => { url.searchParams.set('width', width); return url.toString(); };
  const maxW = Math.min(w, MAX_W);
  const widths = WIDTHS.filter((x) => x < maxW).concat(maxW);
  return {
    kind: 'image',
    src: at(maxW),
    srcset: widths.map((x) => `${at(x)} ${x}w`).join(', '),
    width: maxW,
    height: Math.round((h * maxW) / w),
    alt: img.alt || '',
    // Already-painted gallery image as the placeholder: no blank frame while the zoom rendition loads.
    msrc: img.complete && img.currentSrc ? img.currentSrc : undefined,
    element: img, // PhotoSwipe animates open/close from this element's box
    thumbCropped: true // gallery images are object-fit: cover
  };
}

// Spinner shown until the video can actually play (see activate / wireMediaEvents).
const LOADER = '<span class="pdp-mv-media__loader" role="status" aria-label="Loading video"></span>';

function videoItem(slide) {
  const v = slide.querySelector('video');
  if (!v) return null;
  const sources = Array.from(v.querySelectorAll('source')).map((s) => ({ src: s.src, type: s.type || '' }));
  const mp4 = sources.find((s) => /mp4/i.test(s.type));
  const hls = sources.find((s) => /mpegurl/i.test(s.type));
  const canHls = !!v.canPlayType('application/vnd.apple.mpegurl');
  const src = (mp4 && mp4.src) || (hls && canHls && hls.src) || v.currentSrc || (hls && hls.src);
  if (!src) return null;
  const box = slide.querySelector('.media-box');
  const pct = box ? parseFloat(getComputedStyle(box).getPropertyValue('--media-height')) : NaN;
  const ar = pct > 0 ? 100 / pct : (v.videoWidth && v.videoHeight ? v.videoWidth / v.videoHeight : 16 / 9);
  return {
    kind: 'video',
    html:
      `<div class="pdp-mv-media" style="--ar:${ar.toFixed(4)}"><div class="pdp-mv-media__box">` +
      `<video class="pdp-mv-media__video" playsinline loop preload="none" data-src="${esc(src)}" poster="${esc(v.getAttribute('poster') || '')}"></video>` +
      `<span class="pdp-mv-media__state" aria-hidden="true"></span>` +
      LOADER +
      `<span class="pdp-mv-media__bar" aria-hidden="true"><i></i></span>` +
      `<button type="button" class="pdp-mv-media__mute" aria-label="Mute" aria-pressed="false">${UNMUTED_SVG}${MUTED_SVG}</button>` +
      `</div></div>`
  };
}

function youtubeItem(slide) {
  const id = slide.getAttribute('data-video-id');
  if (!id) return null;
  return {
    kind: 'youtube',
    html: `<div class="pdp-mv-media pdp-mv-media--yt"><div class="pdp-mv-media__box" data-yt="${esc(id)}">${LOADER}</div></div>`
  };
}

function collect(sliderEl) {
  const items = [];
  const map = []; // viewer index -> main slider index
  slidesOf(sliderEl).forEach((slide, i) => {
    const type = slide.getAttribute('data-media-type');
    let item = null;
    if (type === 'image') item = imageItem(slide);
    else if (type === 'video') item = videoItem(slide);
    else if (type === 'external_video') item = youtubeItem(slide);
    if (item) { items.push(item); map.push(i); }
  });
  return { items, map };
}

/* ---------- media slides (video / YouTube) ---------- */

let muted = false;

function mediaParts(content) {
  const el = content && content.element;
  return el ? { video: el.querySelector('video'), yt: el.querySelector('[data-yt]'), root: el.querySelector('.pdp-mv-media') } : {};
}

function setMuted(viewerEl, value) {
  muted = value;
  viewerEl.querySelectorAll('.pdp-mv-media__video').forEach((v) => { v.muted = value; });
  viewerEl.querySelectorAll('.pdp-mv-media__mute').forEach((b) => {
    b.setAttribute('aria-pressed', String(value));
    b.setAttribute('aria-label', value ? 'Unmute' : 'Mute');
  });
  viewerEl.classList.toggle('pdp-mv--muted', value);
}

function playVideo(viewerEl, v) {
  if (!v.getAttribute('src') && v.dataset.src) v.src = v.dataset.src;
  v.muted = muted;
  const p = v.play();
  if (p && p.catch) {
    p.catch(() => {
      // Autoplay with sound refused (e.g. the gesture expired while PhotoSwipe loaded): start muted.
      if (!v.muted) { setMuted(viewerEl, true); v.play().catch(() => {}); }
    });
  }
}

function activate(viewerEl, content) {
  const { video, yt, root } = mediaParts(content);
  if (video) {
    // Loading until the first frame plays; readyState 3+ means it can start right away.
    if (root && video.readyState < 3) root.classList.add('is-loading');
    playVideo(viewerEl, video);
  }
  if (yt && !yt.querySelector('iframe')) {
    if (root) root.classList.add('is-loading');
    const f = document.createElement('iframe');
    f.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(yt.dataset.yt)}?autoplay=1&playsinline=1&rel=0&modestbranding=1`;
    f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    f.title = 'Product video';
    // The player page has arrived; YouTube's own UI takes over from here.
    f.addEventListener('load', () => { if (root) root.classList.remove('is-loading'); }, { once: true });
    yt.appendChild(f);
  }
}

function deactivate(content) {
  const { video, yt, root } = mediaParts(content);
  if (video) video.pause();
  // Drop the iframe: stops playback and frees the player (the loader stays for next time).
  if (yt) yt.querySelectorAll('iframe').forEach((f) => f.remove());
  if (root) root.classList.remove('is-loading');
}

function wireMediaEvents(pswp) {
  const el = pswp.element;
  let downX = 0, downY = 0;
  el.addEventListener('pointerdown', (e) => { downX = e.clientX; downY = e.clientY; }, true);
  el.addEventListener('click', (e) => {
    const mute = e.target.closest('.pdp-mv-media__mute');
    if (mute) { setMuted(el, !muted); return; }
    if (Math.abs(e.clientX - downX) > 8 || Math.abs(e.clientY - downY) > 8) return; // it was a swipe
    // Empty area around a video closes, like the background around an image.
    if (e.target.classList.contains('pdp-mv-media')) { pswp.close(); return; }
    const box = e.target.closest('.pdp-mv-media__box');
    if (!box) return;
    const v = box.querySelector('video');
    if (!v) return;
    if (v.paused) playVideo(el, v); else v.pause();
  });
  // Media events don't bubble; capture them on the viewer root.
  const setState = (e) => {
    const root = e.target.closest && e.target.closest('.pdp-mv-media');
    if (root) root.classList.toggle('is-paused', e.target.paused);
  };
  el.addEventListener('play', setState, true);
  el.addEventListener('pause', setState, true);
  // Loader: on while the video waits for data, off once frames are playing.
  const setLoading = (on) => (e) => {
    const root = e.target.closest && e.target.closest('.pdp-mv-media');
    if (root) root.classList.toggle('is-loading', on);
  };
  el.addEventListener('waiting', setLoading(true), true);
  el.addEventListener('playing', setLoading(false), true);
  el.addEventListener('canplay', (e) => { if (e.target.paused) setLoading(false)(e); }, true);
  el.addEventListener('error', setLoading(false), true);
  el.addEventListener('timeupdate', (e) => {
    const v = e.target;
    const bar = v.duration && v.parentNode.querySelector('.pdp-mv-media__bar i');
    if (bar) bar.style.transform = `scaleX(${v.currentTime / v.duration})`;
  }, true);
}

/* ---------- viewer ---------- */

function registerPager(pswp) {
  pswp.on('uiRegister', () => {
    pswp.ui.registerElement({
      name: 'mvPager',
      className: 'pdp-mv-pager',
      order: 9,
      appendTo: 'wrapper',
      html:
        `<button type="button" class="pdp-mv-pager__btn" data-dir="-1" aria-label="Previous">${ARROW_SVG('15 18 9 12 15 6')}</button>` +
        `<span class="pdp-mv-pager__count" aria-live="polite"></span>` +
        `<button type="button" class="pdp-mv-pager__btn" data-dir="1" aria-label="Next">${ARROW_SVG('9 18 15 12 9 6')}</button>`,
      onInit: (el, p) => {
        const count = el.querySelector('.pdp-mv-pager__count');
        const total = p.getNumItems();
        el.hidden = total < 2;
        el.addEventListener('click', (e) => {
          const b = e.target.closest('[data-dir]');
          if (b) (b.dataset.dir === '1' ? p.next() : p.prev());
        });
        const update = () => { count.textContent = `${p.currIndex + 1} / ${total}`; };
        p.on('change', update);
        update();
      }
    });
  });
}

// Room for the close button (top-right) and the pager pill (bottom) around the image.
const padding = (viewport) =>
  viewport.x < 750 ? { top: 68, bottom: 80, left: 0, right: 0 } : { top: 24, bottom: 88, left: 80, right: 80 };

let opening = false;

async function open(wrap, sliderIndex, trigger) {
  if (opening) return;
  opening = true;
  const inline = wrap.__mvInlineCtl;
  let opened = false;
  if (inline) inline.pause(); // the viewer plays it with sound; free the muted gallery copy
  wrap.classList.add('is-mv-loading');
  try {
    const PhotoSwipe = await loadLib(wrap);
    await afterPaint();
    const sliderEl = wrap.querySelector('.main-slider');
    const { items, map } = collect(sliderEl);
    if (!items.length) return;
    let index = map.indexOf(sliderIndex);
    if (index < 0) index = 0;

    const pswp = new PhotoSwipe({
      dataSource: items,
      index,
      mainClass: 'pdp-mv',
      bgOpacity: 1, // solid white, like the reference PDP
      showHideAnimationType: 'zoom',
      // Gallery originals are ~1280px, which "fit" already exceeds on large screens, so the
      // default click zoom (1 = native size) would be a zoom-out and 'zoom-or-close' would close.
      // Zoom relative to the fitted size instead: click → 2x fit, pinch/wheel up to 3x fit.
      secondaryZoomLevel: (zoomLevels) => Math.max(zoomLevels.initial * 2, 1),
      maxZoomLevel: (zoomLevels) => Math.max(zoomLevels.initial * 3, 2),
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      closeSVG: CLOSE_SVG,
      closeTitle: 'Close',
      paddingFn: padding
    });

    registerPager(pswp);
    pswp.on('contentActivate', ({ content }) => activate(pswp.element, content));
    pswp.on('contentDeactivate', ({ content }) => deactivate(content));
    // On video slides a tap toggles play/pause (handled in wireMediaEvents), not the UI.
    pswp.on('tapAction', (e) => {
      if (pswp.currSlide && pswp.currSlide.data.kind !== 'image') e.preventDefault();
    });
    pswp.on('afterInit', () => {
      wireMediaEvents(pswp);
      setMuted(pswp.element, muted);
    });
    // Land the page gallery on the slide the shopper finished on, before the close animation
    // measures where to shrink back to.
    pswp.on('close', () => {
      const target = map[pswp.currIndex];
      const swiper = sliderEl && sliderEl.swiper;
      if (swiper && typeof target === 'number' && swiper.activeIndex !== target) swiper.slideTo(target, 0);
      track('fullscreen_close', { media_index: target + 1, media_type: items[pswp.currIndex].kind });
    });
    pswp.on('destroy', () => {
      pswp.element && pswp.element.querySelectorAll('video').forEach((v) => v.pause());
      if (inline) inline.resume();
    });

    pswp.init();
    opened = true;
    track('fullscreen_open', { media_index: map[index] + 1, media_type: items[index].kind, trigger });
  } catch (err) {
    // Library failed to load (offline / blocked) — leave the gallery as it was.
  } finally {
    wrap.classList.remove('is-mv-loading');
    opening = false;
    if (inline && !opened) inline.resume();
  }
}

/* ---------- gallery: muted inline autoplay of the video slide in view ---------- */

const INLINE_DELAY = 900; // ms a video slide must stay in view before it starts, so swiping past costs nothing
const YT_ORIGIN = 'https://www.youtube-nocookie.com';
// YouTube's mobile player shows its own controls (title, pause/prev/next, "More videos") for the
// first ~3.6-4.6s of playback even with controls=0 (measured 2026-10-01). The poster stays in
// front until they have faded, so shoppers only ever see the clean video.
const YT_CHROME_MS = 4300;

function inlineAllowed() {
  try {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    const c = navigator.connection;
    if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return false;
  } catch (e) {}
  return true;
}

// The embed reports its state once we say we're listening (what iframe_api does, without
// loading it). We only need "playing", to swap the poster for the video with no black frame.
let ytListening = false;
function listenYouTube() {
  if (ytListening) return;
  ytListening = true;
  window.addEventListener('message', (e) => {
    if (e.origin !== YT_ORIGIN || typeof e.data !== 'string') return;
    let d;
    try { d = JSON.parse(e.data); } catch (err) { return; }
    const playing = (d.event === 'onStateChange' && d.info === 1) ||
      (d.event === 'infoDelivery' && d.info && d.info.playerState === 1);
    if (!playing) return;
    document.querySelectorAll('iframe.pdp-mv-inline').forEach((f) => {
      if (f.contentWindow !== e.source || f.__revealT) return;
      const slide = f.closest('.swiper-slide');
      if (!slide) return;
      f.__revealT = setTimeout(() => {
        if (slide.__mvInline === f) slide.classList.add('is-inline-playing');
      }, YT_CHROME_MS);
    });
  });
}

// Plays the gallery's video slide (muted, looping) while it is the slide in view, and removes it
// as soon as it isn't. Nothing starts before the page's main image has painted;
// paused while the full-screen viewer is open (the viewer plays it with sound).
function inlineController(wrap) {
  const sliderEl = wrap.querySelector('.main-slider');
  if (!sliderEl || !('IntersectionObserver' in window) || !inlineAllowed()) return null;
  const visible = new Set();
  const timers = new Map();
  let ready = false;
  let paused = false;

  const start = (slide) => {
    if (slide.__mvInline) return;
    const type = slide.getAttribute('data-media-type');
    if (type === 'external_video') {
      const host = slide.querySelector('.youtube-lazy');
      const id = host && host.dataset.videoId;
      if (!id) return;
      listenYouTube();
      const f = document.createElement('iframe');
      const vid = encodeURIComponent(id);
      f.className = 'pdp-mv-inline';
      f.src = `${YT_ORIGIN}/embed/${vid}?autoplay=1&mute=1&controls=0&playsinline=1&loop=1&playlist=${vid}` +
        `&rel=0&modestbranding=1&disablekb=1&iv_load_policy=3&fs=0&enablejsapi=1&origin=${encodeURIComponent(location.origin)}`;
      f.allow = 'autoplay; encrypted-media';
      f.title = 'Product video preview';
      f.tabIndex = -1;
      f.setAttribute('aria-hidden', 'true');
      // Cover the box: a 16:9 player in a narrower box (the gallery is 4:3) gets the box's full
      // height and a proportionally wider width, centred and cropped at the sides (no bars).
      const r = host.getBoundingClientRect();
      if (r.width && r.height && r.width / r.height < 16 / 9) {
        f.style.width = `${((r.height * 16) / 9 / r.width) * 100}%`;
      }
      f.addEventListener('load', () => {
        try { f.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), YT_ORIGIN); } catch (e) {}
      }, { once: true });
      // Under the poster, not over it: the poster (our element) stays on top as the touch
      // surface and just fades out once the video plays, so swipes and taps on the video slide
      // land on the page on every browser (iOS Safari included), never inside YouTube's frame.
      host.insertBefore(f, host.firstChild);
      // Plus a clear, never-faded touch layer on top. Once the video plays the poster is at
      // opacity 0, and iPhone Safari (which decides "page scroll or gallery swipe" from the layers
      // under the finger) can then read YouTube's frame instead of our pan-y gallery and cancel the
      // swipe mid-drag. This layer keeps our surface on top; taps on it still open the viewer.
      const shield = document.createElement('span');
      shield.className = 'pdp-mv-shield';
      shield.setAttribute('aria-hidden', 'true');
      host.appendChild(shield);
      f.__shield = shield;
      slide.__mvInline = f;
    } else if (type === 'video') {
      const v = slide.querySelector('video');
      if (!v) return;
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.addEventListener('playing', () => { if (slide.__mvInline === v) slide.classList.add('is-inline-playing'); }, { once: true });
      v.play().catch(() => {});
      slide.__mvInline = v;
    }
  };

  const stop = (slide) => {
    clearTimeout(timers.get(slide));
    timers.delete(slide);
    const el = slide.__mvInline;
    if (!el) return;
    slide.__mvInline = null;
    slide.classList.remove('is-inline-playing');
    if (el.tagName === 'IFRAME') {
      clearTimeout(el.__revealT);
      // Let the poster fade back in first, then free the player entirely.
      setTimeout(() => { el.remove(); if (el.__shield) el.__shield.remove(); }, 350);
    } else {
      el.pause();
    }
  };

  // Never boot the player while a finger is on the gallery or a slide is still moving: its
  // startup scripts would compete with the swipe for the main thread.
  let interacting = false;
  const ENDS = ['pointerup', 'pointercancel', 'touchend', 'touchcancel'];
  const release = () => {
    interacting = false;
    ENDS.forEach((t) => document.removeEventListener(t, release));
    visible.forEach(schedule);
  };
  const press = () => {
    if (interacting) return;
    interacting = true;
    ENDS.forEach((t) => document.addEventListener(t, release, { passive: true }));
  };
  sliderEl.addEventListener('pointerdown', press, { passive: true });
  sliderEl.addEventListener('touchstart', press, { passive: true });

  const schedule = (slide) => {
    clearTimeout(timers.get(slide));
    timers.set(slide, setTimeout(() => {
      timers.delete(slide);
      if (!ready || paused || !visible.has(slide) || document.visibilityState !== 'visible') return;
      if (interacting || (sliderEl.swiper && sliderEl.swiper.animating)) { schedule(slide); return; }
      // The YouTube player's startup is a burst of main-thread work (on Android it usually shares
      // the page's thread): begin it only when the thread is idle, re-checking nothing changed.
      const go = () => {
        if (!paused && !interacting && visible.has(slide) && document.visibilityState === 'visible') start(slide);
      };
      if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 1500 });
      else go();
    }, INLINE_DELAY));
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (en.isIntersecting && en.intersectionRatio >= 0.6) {
        visible.add(en.target);
        schedule(en.target);
      } else {
        visible.delete(en.target);
        stop(en.target);
      }
    });
  }, { threshold: [0, 0.6] });

  const observeAll = () => {
    slidesOf(sliderEl).forEach((s) => {
      const t = s.getAttribute('data-media-type');
      if ((t === 'video' || t === 'external_video') && !s.__mvObserved) {
        s.__mvObserved = true;
        io.observe(s);
      }
    });
  };
  observeAll();
  const rail = sliderEl.querySelector('.swiper-wrapper');
  if (rail) new MutationObserver(observeAll).observe(rail, { childList: true });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') visible.forEach(stop);
    else visible.forEach(schedule);
  });

  // Ready once the main image has painted (theme.liquid's __scAfterLcp), not at window load:
  // load waits for every tag and app on the PDP (11-29s measured), so shoppers only saw the poster.
  const go = () => { ready = true; visible.forEach(schedule); };
  if (window.__scAfterLcp) window.__scAfterLcp(go);
  else if (document.readyState === 'complete') go();
  else window.addEventListener('load', go, { once: true });

  return {
    pause() { paused = true; visible.forEach(stop); },
    resume() { paused = false; visible.forEach(schedule); }
  };
}

/* ---------- wiring ---------- */

function activeIndex(wrap) {
  const sliderEl = wrap.querySelector('.main-slider');
  if (sliderEl && sliderEl.swiper) return sliderEl.swiper.activeIndex;
  const slides = slidesOf(sliderEl);
  const i = slides.findIndex((s) => s.classList.contains('swiper-slide-active'));
  return i < 0 ? 0 : i;
}

function wire(wrap) {
  if (wrap.__pdpMv) return;
  wrap.__pdpMv = true;
  const warm = () => { loadLib(wrap).catch(() => {}); };
  wrap.addEventListener('pointerenter', warm, { once: true, passive: true });
  wrap.addEventListener('touchstart', warm, { once: true, passive: true });
  wrap.addEventListener('focusin', warm, { once: true }); // keyboard users: start loading on focus

  // Zoom / full-view button over the main image.
  wrap.addEventListener('click', (e) => {
    const btn = e.target.closest('.pdp-mv-open');
    if (btn) { e.preventDefault(); open(wrap, activeIndex(wrap), 'button'); }
  });

  const sliderEl = wrap.querySelector('.main-slider');
  if (sliderEl) {
    // Tap on a video slide (poster or its play button) opens the video in the big viewer
    // instead of playing inline. Capture phase, so it runs before the inline YouTube
    // loader's button listener; the tap that ends a swipe (swiper.allowClick === false) is ignored.
    sliderEl.addEventListener('click', (e) => {
      const slide = e.target.closest('.swiper-slide');
      const type = slide && slide.getAttribute('data-media-type');
      if (type !== 'video' && type !== 'external_video') return;
      if (sliderEl.swiper && sliderEl.swiper.allowClick === false) return;
      e.preventDefault();
      e.stopPropagation();
      open(wrap, slidesOf(sliderEl).indexOf(slide), 'video');
    }, true);
    // Click / tap on the main image. Swiper swallows the click that ends a drag, so a swipe
    // never opens the viewer.
    sliderEl.addEventListener('click', (e) => {
      if (e.target.closest('button, a, video, iframe, model-viewer')) return;
      const slide = e.target.closest('.swiper-slide');
      if (!slide || slide.getAttribute('data-media-type') !== 'image') return;
      open(wrap, slidesOf(sliderEl).indexOf(slide), 'image');
    });
    // Two-finger touch on the gallery (the page can't pinch-zoom it — touch-action: pan-y)
    // opens the zoom viewer instead.
    sliderEl.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 2) return;
      const slide = e.target.closest('.swiper-slide');
      if (slide && slide.getAttribute('data-media-type') === 'image') open(wrap, slidesOf(sliderEl).indexOf(slide), 'pinch');
    }, { passive: true });
  }
  // Thumbnails (image or video) only select their slide in the main gallery; the viewer
  // opens from the main slide itself.

  // The video slide in view plays muted in the gallery; a tap still opens the viewer.
  wrap.__mvInlineCtl = inlineController(wrap);
}

document.querySelectorAll('.cst-product-slider-wrapper[data-mv-js]').forEach(wire);
